import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, Home, User, Briefcase, Mail, Award } from 'lucide-react';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [isMobile, setIsMobile] = useState(false);
  const observerRef = useRef(null);

  const navItems = [
    { id: 'home', label: 'Home', icon: Home },
    { id: 'about', label: 'About', icon: User },
    { id: 'projects', label: 'Projects', icon: Briefcase },
    { id: 'experience', label: 'Experience', icon: Award },
    { id: 'contact', label: 'Contact', icon: Mail },
  ];

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsOpen(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Intersection Observer for scroll-based active state
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '-20% 0px -70% 0px',
      threshold: 0.1
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    }, options);

    // Observe all sections
    navItems.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) {
        observerRef.current.observe(element);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen && isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, isMobile]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      // Manually set active state immediately for better UX
      setActiveSection(sectionId);
      
      // Smooth scroll to section
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
    setIsOpen(false);
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      <nav style={styles.nav}>
        <div style={styles.container}>
          <div style={styles.navContent}>
            {/* Logo */}
            <button
              onClick={() => scrollToSection('home')}
              style={styles.logo}
              onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
              onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
            >
              RNR
            </button>

            {/* Desktop Navigation */}
            {!isMobile && (
              <div style={styles.desktopNav}>
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeSection === item.id;
                  
                  return (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      style={{
                        ...styles.navButton,
                        ...(isActive ? styles.activeNavButton : {}),
                        color: isActive ? '#96C7B9' : '#546C81',
                        backgroundColor: isActive ? 'rgba(150, 199, 185, 0.15)' : 'transparent',
                        fontWeight: isActive ? '600' : '500'
                      }}
                      onMouseOver={(e) => {
                        if (!isActive) {
                          e.target.style.color = '#96C7B9';
                          e.target.style.backgroundColor = 'rgba(150, 199, 185, 0.1)';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (!isActive) {
                          e.target.style.color = '#546C81';
                          e.target.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <Icon size={16} />
                      {item.label}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Mobile menu button */}
            {isMobile && (
              <button
                onClick={toggleMenu}
                style={styles.mobileMenuButton}
                onMouseOver={(e) => e.target.style.backgroundColor = 'rgba(150, 199, 185, 0.2)'}
                onMouseOut={(e) => e.target.style.backgroundColor = 'rgba(150, 199, 185, 0.1)'}
              >
                {isOpen ? <X size={24} color="#546C81" /> : <Menu size={24} color="#546C81" />}
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile Navigation Overlay */}
      {isMobile && (
        <div 
          style={{
            ...styles.mobileOverlay,
            opacity: isOpen ? 1 : 0,
            visibility: isOpen ? 'visible' : 'hidden',
            transform: isOpen ? 'translateX(0)' : 'translateX(-100%)'
          }}
        >
          <div style={styles.mobileMenu}>
            {navItems.map((item, index) => {
              const Icon = item.icon;
              const isActive = activeSection === item.id;
              
              return (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  style={{
                    ...styles.mobileNavButton,
                    ...(isActive ? styles.activeMobileNavButton : {}),
                    transitionDelay: isOpen ? `${index * 0.1}s` : '0s',
                    backgroundColor: isActive ? 'rgba(150, 199, 185, 0.2)' : 'rgba(255, 255, 255, 0.9)',
                    borderColor: isActive ? '#96C7B9' : 'rgba(150, 199, 185, 0.3)',
                    color: isActive ? '#96C7B9' : '#546C81',
                    fontWeight: isActive ? '700' : '600'
                  }}
                  onTouchStart={(e) => {
                    if (!isActive) {
                      e.target.style.backgroundColor = 'rgba(150, 199, 185, 0.2)';
                      e.target.style.transform = 'scale(0.95)';
                    }
                  }}
                  onTouchEnd={(e) => {
                    if (!isActive) {
                      e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
                      e.target.style.transform = 'scale(1)';
                    }
                  }}
                >
                  <Icon size={24} />
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Mobile backdrop */}
      {isMobile && isOpen && (
        <div 
          style={styles.backdrop}
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
};

const styles = {
  nav: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    borderBottom: '1px solid rgba(150, 199, 185, 0.3)',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    height: '70px'
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 1rem',
    height: '100%'
  },
  navContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '100%'
  },
  logo: {
    fontSize: '2rem',
    fontWeight: 'normal',
    fontFamily: 'Balldoug, serif',
    background: 'linear-gradient(135deg, #96C7B9 0%, #7BB5A7 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    letterSpacing: '0.05em',
    padding: '0.5rem'
  },
  desktopNav: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  },
  navButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.75rem 1rem',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '12px',
    fontSize: '0.9rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    whiteSpace: 'nowrap',
    position: 'relative'
  },
  activeNavButton: {
    boxShadow: '0 2px 8px rgba(150, 199, 185, 0.3)',
    transform: 'translateY(-1px)'
  },
  mobileMenuButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '48px',
    height: '48px',
    backgroundColor: 'rgba(150, 199, 185, 0.1)',
    border: '1px solid rgba(150, 199, 185, 0.3)',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  mobileOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 999,
    backgroundColor: 'rgba(209, 240, 224, 0.98)',
    backdropFilter: 'blur(15px)',
    transition: 'all 0.3s ease-in-out'
  },
  mobileMenu: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: '2rem',
    gap: '1rem'
  },
  mobileNavButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1rem',
    width: '100%',
    maxWidth: '300px',
    padding: '1.25rem 2rem',
    border: '2px solid rgba(150, 199, 185, 0.3)',
    borderRadius: '16px',
    fontSize: '1.1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
    touchAction: 'manipulation'
  },
  activeMobileNavButton: {
    boxShadow: '0 6px 20px rgba(150, 199, 185, 0.4)',
    transform: 'translateY(-2px)'
  },
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 998,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    opacity: 0.5
  }
};

export default Navigation;
